import { Check } from "lucide-react";

// HTML mirror of the backend A4 PDF; `content` = site content (or a draft of it)
export default function InvoicePreview({ sale, content, docType = "invoice", currency = "LEK" }) {
    const lekRate = content.currency_lek_per_eur || 100;
    const fmt = (eur) => {
        const v = parseFloat(eur) || 0;
        if (currency === "LEK") return `${Math.round(v * lekRate).toLocaleString()} Lek`;
        return `€${v.toFixed(2)}`;
    };
    const cols = [
        { key: "part_number", label: "Kodi", enabled: content.invoice_show_part_number },
        { key: "name", label: "Përshkrimi", enabled: content.invoice_show_name },
        { key: "description", label: "Detaje", enabled: content.invoice_show_description },
        { key: "category", label: "Kategoria", enabled: content.invoice_show_category },
        { key: "qty", label: "Sasia", enabled: content.invoice_show_qty, align: "right" },
        { key: "unit_price", label: currency === "LEK" ? "Çmimi (Lek)" : "Çmimi (€)", enabled: content.invoice_show_unit_price, align: "right" },
        { key: "line_total", label: currency === "LEK" ? "Totali (Lek)" : "Totali (€)", enabled: content.invoice_show_line_total, align: "right" },
    ].filter((c) => c.enabled);
    const logo = content.invoice_logo_url || content.logo_url;
    const reasons = (sale.failure_reasons || []).filter(Boolean);
    const serviceNote = (sale.service_note || "").trim();
    const services = (sale.services || []).filter(Boolean);
    const isPrev = docType === "preventiv";
    const title = (isPrev ? content.invoice_title_preventiv : content.invoice_title_invoice) || (isPrev ? "PREVENTIV" : "FATURË");
    const subtitle = ((isPrev ? content.invoice_subtitle_preventiv : content.invoice_subtitle_invoice) || "").trim();

    return (
        <div className="admin-light relative p-8 bg-white" data-testid="invoice-preview">
            {content.invoice_background_url && (
                <img src={content.invoice_background_url} alt="" className="absolute inset-[10%] w-[80%] h-[80%] object-contain pointer-events-none" style={{ opacity: content.invoice_background_opacity ?? 0.08 }} />
            )}
            <div className="relative">
                <div className="flex justify-between items-start border-b border-slate-200 pb-6 mb-6">
                    <div className="flex items-start gap-4">
                        {logo && <img src={logo} alt="logo" className="w-20 h-20 object-contain" />}
                        <div>
                            <div className="font-display font-black uppercase text-lg">{content.invoice_company_name || content.brand_name}</div>
                            <div className="text-xs text-slate-600 space-y-0.5 mt-1">
                                {content.invoice_company_address && <div>{content.invoice_company_address}</div>}
                                {content.invoice_company_city && <div>{content.invoice_company_city}</div>}
                                {content.invoice_company_nipt && <div>NIPT: {content.invoice_company_nipt}</div>}
                                {content.invoice_company_phone && <div>{content.invoice_company_phone}</div>}
                                {content.invoice_company_email && <div>{content.invoice_company_email}</div>}
                            </div>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="font-display font-black uppercase text-2xl" data-testid="invoice-preview-title">{title}</div>
                        {subtitle && <div className="text-xs italic text-slate-600 whitespace-pre-line mt-0.5" data-testid="invoice-preview-subtitle">{subtitle}</div>}
                        <div className="font-mono text-xs text-slate-600 mt-1">
                            Nr. {sale.id.slice(0, 8).toUpperCase()}<br />Data: {sale.created_at.slice(0, 10)}
                        </div>
                    </div>
                </div>

                <div className="mb-6">
                    <div className="label-kicker text-slate-500 mb-1">Klienti</div>
                    <div className="font-bold">{sale.customer_name}</div>
                    {sale.customer_phone && <div className="font-mono text-xs text-slate-600">Tel: {sale.customer_phone}</div>}
                </div>

                <table className="w-full text-sm border-collapse mb-6">
                    <thead>
                        <tr className="bg-slate-900 text-white">
                            {cols.map((c2) => (
                                <th key={c2.key} className={`px-2 py-2 label-kicker text-white ${c2.align === "right" ? "text-right" : "text-left"}`}>{c2.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {[...sale.items, ...(sale.service_lines || []).map((l) => ({ part_number: "SHËRBIM", name: l.name, qty: l.qty || 1, unit_price: l.price_eur, line_total: l.line_eur ?? l.price_eur }))].map((it, i) => (
                            <tr key={i} className="border-b border-slate-200">
                                {cols.map((c2) => {
                                    let val = it[c2.key] ?? "";
                                    if (c2.key === "unit_price" || c2.key === "line_total") val = fmt(val);
                                    return <td key={c2.key} className={`px-2 py-2 ${c2.align === "right" ? "text-right" : ""}`}>{val}</td>;
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="flex justify-end">
                    <div className="w-72 space-y-1 text-sm">
                        <div className="flex justify-between"><span className="text-slate-600">Nëntotali</span><span>{fmt(sale.subtotal)}</span></div>
                        {sale.discount_percent > 0 && (
                            <div className="flex justify-between text-red-600">
                                <span>Zbritje ({sale.discount_percent}%)</span><span>-{fmt(sale.discount_amount)}</span>
                            </div>
                        )}
                        {sale.services_total > 0 && (
                            <div className="flex justify-between"><span className="text-slate-600">Shërbimet</span><span data-testid="invoice-preview-services-total">{fmt(sale.services_total)}</span></div>
                        )}
                        <div className="flex justify-between border-t border-slate-300 pt-2 font-display font-black text-lg">
                            <span>TOTALI</span><span data-testid="invoice-preview-total">{fmt(sale.total)}</span>
                        </div>
                    </div>
                </div>

                {reasons.length > 0 && (
                    <div className="mt-8" data-testid="invoice-preview-reasons">
                        <div className="font-bold text-sm border-b border-slate-400 pb-1 mb-2">{content.invoice_reasons_title || "Arsyet e prishjes"}</div>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                            {reasons.map((r) => (
                                <div key={r} className="flex items-center gap-2">
                                    <span className="w-3.5 h-3.5 border border-slate-900 flex items-center justify-center shrink-0">
                                        <Check className="w-3 h-3" strokeWidth={3} />
                                    </span>
                                    {r}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {(serviceNote || services.length > 0) && (
                    <div className="mt-6" data-testid="invoice-preview-service-note">
                        <div className="font-bold text-sm border-b border-slate-400 pb-1 mb-2">{content.invoice_service_note_title || "Shërbimet që duhen bërë te makina"}</div>
                        {services.length > 0 && (
                            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs mb-2" data-testid="invoice-preview-services">
                                {services.map((r) => (
                                    <div key={r} className="flex items-center gap-2">
                                        <span className="w-3.5 h-3.5 border border-slate-900 flex items-center justify-center shrink-0"><Check className="w-3 h-3" strokeWidth={3} /></span>
                                        {r}
                                    </div>
                                ))}
                            </div>
                        )}
                        {serviceNote && <div className="text-xs whitespace-pre-line">{serviceNote}</div>}
                    </div>
                )}

                {(content.invoice_company_iban || content.invoice_company_bank) && (
                    <div className="mt-8 pt-4 border-t border-slate-200 text-xs text-slate-600">
                        {content.invoice_company_iban && <div>IBAN: {content.invoice_company_iban}</div>}
                        {content.invoice_company_bank && <div>Banka: {content.invoice_company_bank}</div>}
                    </div>
                )}
                {content.invoice_footer_text && (
                    <div className="mt-6 text-xs text-slate-500 italic whitespace-pre-line">{content.invoice_footer_text}</div>
                )}
            </div>
        </div>
    );
}
